import {
  type IconProps,
  InfoIcon,
  LightbulbIcon,
  LightningIcon,
  StarIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import type { ComponentType } from "react";

import { cn } from "@/lib/utils";

export type CalloutType =
  | "info"
  | "tip"
  | "warning"
  | "important"
  | "recommended_action";

export interface CalloutProps {
  /** Variant: left border + wash map to primary / chart tokens; recommended_action uses chart-1. */
  type?: CalloutType | null;
  /** Optional title above the content. */
  title?: string | null;
  /** Body text. */
  content: string;
  /** Optional class name for the root. */
  className?: string;
}

interface CalloutVariantConfig {
  icon: ComponentType<IconProps>;
  border: string;
  bg: string;
  iconColor: string;
}

/** Matches chat CalloutHandler: border-l-4, rounded-r-lg, primary/chart semantic colors. */
const VARIANTS: Record<NonNullable<CalloutType>, CalloutVariantConfig> = {
  info: {
    icon: InfoIcon,
    border: "border-l-primary",
    bg: "bg-primary/5",
    iconColor: "text-primary",
  },
  tip: {
    icon: LightbulbIcon,
    border: "border-l-chart-2",
    bg: "bg-chart-2/5",
    iconColor: "text-chart-2",
  },
  warning: {
    icon: WarningIcon,
    border: "border-l-chart-5",
    bg: "bg-chart-5/5",
    iconColor: "text-chart-5",
  },
  important: {
    icon: StarIcon,
    border: "border-l-chart-4",
    bg: "bg-chart-4/5",
    iconColor: "text-chart-4",
  },
  recommended_action: {
    icon: LightningIcon,
    border: "border-l-chart-1",
    bg: "bg-chart-1/5",
    iconColor: "text-chart-1",
  },
};

/** Icon width (`w-5`) + `gap-1.5` so body lines up with title text, not under the icon. */
const BODY_INDENT_WHEN_TITLE = "pl-[calc(1.25rem+0.375rem)]";

/**
 * Callout with left accent border and semantic colors. With a title, icon and title sit in one
 * flex row with `items-center` and `gap-1.5`; body is indented (`w-5` + gap) so it lines up with the title.
 */
const Callout = ({
  type = "info",
  title,
  content,
  className,
}: CalloutProps) => {
  const resolvedType = type ?? "info";
  const variant = VARIANTS[resolvedType];
  const Icon = variant.icon;
  const hasTitle = title != null && title !== "";
  const iconWeight = resolvedType === "important" ? "fill" : "regular";
  const iconClass = cn("h-5 w-5 shrink-0", variant.iconColor);

  return (
    <div
      className={cn(
        "rounded-r-lg border-l-4 p-4",
        variant.border,
        variant.bg,
        className,
      )}
    >
      {hasTitle ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <Icon className={iconClass} weight={iconWeight} />
            <p className="m-0 min-w-0 flex-1 font-semibold text-foreground text-sm leading-tight">
              {title}
            </p>
          </div>
          <p
            className={cn(
              "m-0 min-w-0 text-sm text-muted-foreground",
              BODY_INDENT_WHEN_TITLE,
            )}
          >
            {content}
          </p>
        </div>
      ) : (
        <div className="flex items-start gap-1.5">
          <Icon className={iconClass} weight={iconWeight} />
          <p className="m-0 min-w-0 text-sm text-muted-foreground">{content}</p>
        </div>
      )}
    </div>
  );
};

export default Callout;
